import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import CallToAction from '../components/CallToAction';

const ServiceList = ({ title, items }) => {
  return (
    <div className="glass-card p-8 mb-8">
      <h3 className="text-2xl font-bold mb-6">{title}</h3>
      <ul className="space-y-4">
        {items.map((item, index) => (
          <li key={index} className="flex items-start gap-3">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-6 h-6 text-primary flex-shrink-0 mt-0.5">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const PricingItem = ({ service, price, description }) => {
  return (
    <div className="glass-card hover:shadow-soft-lg transition-all duration-300">
      <div className="p-6 border-b border-neutral">
        <h3 className="text-xl font-semibold mb-1">{service}</h3>
        <div className="text-xl font-bold text-primary mb-3">{price}</div>
        <p className="text-secondary-light">{description}</p>
      </div>
    </div>
  );
};

const TimelineItem = ({ title, description, icon }) => {
  return (
    <div className="flex">
      <div className="flex flex-col items-center">
        <div className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center">
          {icon}
        </div>
        <div className="h-full w-0.5 bg-primary/20"></div>
      </div>
      <div className="ml-4 pb-8">
        <h3 className="text-xl font-semibold mb-2">{title}</h3>
        <p className="text-secondary-light">{description}</p>
      </div>
    </div>
  );
};

const ServicesPage = () => {
  const coreServices = [
    "Custom website designed for your business (no templates)",
    "Mobile-optimized and fast-loading",
    "Contact forms and lead capture setup",
    "Google Analytics integration",
    "Multiple revisions included",
    "First draft delivered in 7 days",
    "Final website completed within 14 days"
  ];

  const additionalServices = [
    {
      service: "Advanced SEO Optimization",
      price: "$500-800",
      description: "On-page SEO, keyword optimization, local SEO setup, and Google Business Profile integration."
    },
    {
      service: "Online Booking Systems",
      price: "$300-500",
      description: "Integration with calendly or other booking systems to allow customers to schedule appointments."
    },
    {
      service: "Review Management",
      price: "$400",
      description: "Setup of review collection system and display of reviews on your website."
    },
    {
      service: "Google My Business Setup",
      price: "$200",
      description: "Complete setup and optimization of your Google Business Profile."
    },
    {
      service: "Social Media Integration",
      price: "$300",
      description: "Integration of social media feeds and sharing capabilities on your website."
    }
  ];

  useEffect(() => {
    document.title = "Services & Pricing | Snap Launch Studio Website Development";
  }, []);

  return (
    <>
        <PageHeader 
          title="Everything You Need, Nothing You Don't" 
        />
      
      <section className="py-16 bg-white">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-primary/10 to-accent/5 rounded-3xl p-8 mb-12">
              <div className="flex flex-col md:flex-row justify-between items-center gap-8 mb-8">
                <div>
                  <h2 className="text-3xl font-bold mb-4">Core Package</h2>
                  <p className="text-lg text-secondary-light">Everything you need for a professional online presence</p>
                  <div className="mt-2 bg-primary/20 px-3 py-1 rounded-full inline-block">
                    <span className="text-primary font-semibold">+ $630 Bonus Value FREE</span>
                  </div>
                </div>
                <div className="text-4xl font-bold text-primary">$1,500</div>
              </div>
              
              <ServiceList items={coreServices} />
              
              <div className="mt-8 bg-gradient-to-r from-primary/10 to-accent/10 rounded-xl p-6">
                <h3 className="text-xl font-bold mb-4 flex items-center">
                  <span className="mr-2">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-6 h-6 text-primary">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </span>
                  FREE Bonus Package ($630 Value)
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-6 h-6 text-primary flex-shrink-0 mt-0.5">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>6 months of premium hosting ($50/month x 6 = $300 value)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-6 h-6 text-primary flex-shrink-0 mt-0.5">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>1 hour of ongoing support for small edits and issues ($150 value)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-6 h-6 text-primary flex-shrink-0 mt-0.5">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Complete <Link to="/crm-system" className="text-primary hover:underline">CRM system</Link> with our agency branding for lead management ($50/month x 6 = $300 value)</span>
                  </li>
                </ul>
                <div className="mt-4 font-medium">
                  <p>Total Value: $2,250 - You Pay Only $1,500</p>
                  <p className="mt-2 text-secondary-light">After 6 Months (Optional): Continue with our <Link to="/crm-system" className="text-primary hover:underline">CRM</Link> and hosting package for just $100/month, or take your website anywhere you want - no contracts, no obligations.</p>
                </div>
              </div>
            </div>
            
            <h2 className="text-3xl font-bold mb-8 text-center">Additional Services</h2>
            <div className="grid md:grid-cols-2 gap-6 mb-16">
              {additionalServices.map((service, index) => (
                <PricingItem 
                  key={index}
                  service={service.service}
                  price={service.price}
                  description={service.description}
                />
              ))}
            </div>
            
            <h2 className="text-3xl font-bold mb-8 text-center">Our Process</h2>
            <div className="glass-card p-8">
              <div className="mb-8 last:mb-0">
                <TimelineItem 
                  title="Initial Payment"
                  description="$500 deposit to start the project and show mutual commitment."
                  icon={<span className="font-bold">1</span>}
                />
                
                <TimelineItem 
                  title="First Draft"
                  description="We deliver the first version of your website within 7 days."
                  icon={<span className="font-bold">2</span>}
                />
                
                <TimelineItem 
                  title="Revisions & Feedback"
                  description="We make adjustments based on your feedback to ensure the site meets your needs."
                  icon={<span className="font-bold">3</span>}
                />
                
                <TimelineItem 
                  title="Final Delivery"
                  description="Your completed website is delivered within 14 days of starting."
                  icon={<span className="font-bold">4</span>}
                />
                
                <div className="flex">
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center">
                      <span className="font-bold">5</span>
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-xl font-semibold mb-2">Final Payment</h3>
                    <p className="text-secondary-light">$1,000 final payment upon delivery and your approval.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-12 p-8 bg-primary/10 rounded-xl">
              <div className="flex items-start gap-4">
                <div className="text-primary">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-10 h-10">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-2">100% Money-Back Guarantee</h3>
                  <p className="text-lg">
                    If you're not completely satisfied with your website, we'll refund your $500 deposit. No questions asked. That's how confident we are in our ability to deliver exactly what your business needs.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <CallToAction title="Get a Complete Lead Generation System - Not Just a Website" />
    </>
  );
};

export default ServicesPage;
